import { findNode } from '@futrli/shared-services';

import { REPORT_SECTION } from '../../../../core/constants';
import {
  ConsolidatedForecastReport,
  ConsolidatedForecastReportStructure,
  ConsolidatedNodeStructure
} from '../../../../core/types';

/**
 * Returns the node in the given tree which has the given ref
 * @param tree - Tree nodes array to search
 * @param ref - Ref to search for
 */
export const findNodeByRef = (tree: ConsolidatedNodeStructure[], ref: string) => {
  return findNode(tree, n => n.ref === ref);
};

/**
 * Returns top level node for a specific category
 * @param node_info Node info part of response
 * @param category Category to find the node for
 */
export const getTopLevelStaticCategoryNode = (
  consolidatedForecastReport: ConsolidatedForecastReport,
  category: string,
  reportSection: REPORT_SECTION
) => {
  const tree = consolidatedForecastReport.structure[reportSection];
  const index = consolidatedForecastReport.node_info.by_ref;
  const searchFunc = (n: ConsolidatedNodeStructure) =>
    isTopLevelStaticRowType(index[n.ref].type) && index[n.ref].static_id === category;
  return findNode(tree, searchFunc);
};

/**
 * Returns All node refs from the tree
 * @param tree - Tree nodes to extract the refs from
 */
export const getNodeRefs = (tree: ConsolidatedNodeStructure[] | null): string[] => {
  return tree ? tree.flatMap(node => [node.ref, ...getNodeRefs(node.children)]) : [];
};

/**
 * Returns all node refs for all report sections (profit & loss, balance sheet and cashflow)#
 * @param forecastReport - Forecast report to extract the node refs from
 */
export const getReportNodeRefs = (forecastReport: ConsolidatedForecastReport): string[] => {
  return Object.values(forecastReport.structure).flatMap(nodes => getNodeRefs(nodes));
};

/**
 * Returns the report section that a node with the given ref belongs to
 * @param structure - report structure
 * @param refToFind - ref to look for
 */
export const getReportSectionByNodeRef = (structure: ConsolidatedForecastReportStructure, refToFind: string) => {
  return Object.values(REPORT_SECTION).find(sectionId => {
    return findNode(structure[sectionId] || [], n => n.ref === refToFind);
  });
};

/**
 * Returns true if row type is 'top-level-static' or 'consolidation-static'
 * @param rowType - The row type to test
 */
export const isTopLevelStaticRowType = (rowType: string) => {
  return rowType === 'consolidation-static' || rowType === 'top-level-static';
};
